// Reusable modal component

import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { ReactNode } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  testId?: string;
  /**
   * 'inside' caps the modal at the viewport height and scrolls the body, keeping
   * the header and footer buttons pinned. Use it for forms tall enough to overflow
   * a short viewport, which would otherwise push the footer off-screen.
   */
  scrollBehavior?: 'inside' | 'outside';
}

/**
 * Consistent modal/dialog wrapper with common patterns
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'lg',
  testId,
  scrollBehavior = 'outside',
}: ModalProps) {
  const modalGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );
  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size={size} isCentered scrollBehavior={scrollBehavior}>
      <ModalOverlay backdropFilter="blur(4px)"/>
      <ModalContent bg="bg-surface" bgGradient={modalGradient} data-testid={testId}>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </ChakraModal>
  );
}
